import {
  Avatar,
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PAYMENT_METHODS, getOrderTypeLabel } from "../../constants/enums";
import { mergeItemsByVariant } from "../../reduxStore/selectors/cartSelectors";
import { settleOrder } from "../../reduxStore/slices/cartSlice";
import { calculateBusyTableCount } from "../../reduxStore/slices/tableSlice";
import { formatCurrency } from "../../utils/currency";
import { useNotify } from "../../utils/snackbarUtils";
import AppButton from "../app-button/AppButton";
import AppDialog from "../app-dialog/AppDialog";
import Iconify from "../iconify/Iconify";
import OrderPaymentOptions from "../order-payment-options/OrderPaymentOptions";
import "./PaymentInfo.css";
import PaymentInfoItemCard from "./PaymentInfoItemCard";

function PaymentInfo({ isSettleDialogOpen, orderDetails, onDialogCancel }) {
  const { user } = useSelector((state) => state.auth);
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [totalAmountValue, setTotalAmountValue] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    PAYMENT_METHODS.CASH
  );

  const allItems = mergeItemsByVariant(orderDetails?.kots || []);
  const order = orderDetails?.order;
  const storeId = user?.storeId;

  const dine = orderDetails?.dine;
  const customer = orderDetails?.customer;

  const orderDiscount = orderDetails?.order?.discount;
  const hasDiscount = orderDiscount && orderDiscount?.type !== null;

  console.log("xxxxx", allItems);

  const [dateTime, setDateTime] = useState(new Date());
  // Update every second
  useEffect(() => {
    const timer = setInterval(() => {
      setDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format date → Monday, 15 July 2025
  const weekday = dateTime.toLocaleDateString("en-US", { weekday: "short" });
  const day = dateTime.toLocaleDateString("en-US", { day: "numeric" });
  const month = dateTime.toLocaleDateString("en-US", { month: "short" });
  const year = dateTime.toLocaleDateString("en-US", { year: "numeric" });

  const formattedDate = `${weekday}, ${day} ${month} ${year}`;

  // Format time → 04:44 AM
  const formattedTime = dateTime.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  useEffect(() => {
    setTotalAmount(20 || 0);
    setTotalAmountValue(20 || 0);
    setRemainingAmount(totalAmount - totalAmountValue);
  }, [orderDetails]);

  useEffect(() => {
    setRemainingAmount(totalAmount - totalAmountValue);
  }, [totalAmountValue]);

  const handleAmountChange = (value) => {
    // Allow only numbers and single dot
    if (!/^\d*\.?\d*$/.test(value)) return;

    // Restrict to 2 decimals
    if (value.includes(".")) {
      const [intPart, decimalPart] = value.split(".");
      if (decimalPart.length > 2) return;
    }

    // Prevent leading zero unless it's "0." or "0"
    if (/^0\d+/.test(value)) value = value.replace(/^0+/, "");
    setTotalAmountValue(value);
    // Convert to number
    const numeric = parseFloat(value || "0");

    // Prevent overpayment
    if (numeric > 20) {
      showError("Entered amount cannot exceed total bill.");
      return;
    }
  };

  const handleSettleClick = async () => {
    if (totalAmountValue === "" || totalAmountValue === "0") {
      showError("Please enter a valid amount to settle.");
      return;
    }
    if (totalAmountValue > 20) {
      showError("Entered amount cannot exceed total bill.");
      return;
    }
    try {
      setIsLoading(true);
      const obj = {
        store_id: storeId,
        order_id: orderDetails?.order_id,
        order_uid: orderDetails?.order_uid,
        dine_area_table_uid: dine?.table?.dine_area_table_uid,
        order_status_id: 5,
        payment_options: [
          {
            payment_option_id: selectedPaymentMethod?.value,
            total_amount: 500,
          },
        ],
        total_amount: 1000,
        amount_received: 950,
        order_dine_table_id: dine?.table?.order_dine_table_id,
        order_dine_table_uid: dine?.table?.order_dine_table_uid,
        settleFromCounter: false,
      };
      const res = await dispatch(settleOrder(obj)).unwrap();
      dispatch(calculateBusyTableCount());
      showSuccess(res.message);
      onDialogCancel();
      setIsLoading(false);
    } catch (err) {
      handleApiError(err);
      setIsLoading(false);
    }
  };

  // Handle numpad clicks
  const handleNumpadClick = (val) => {
    setTotalAmountValue((prev) => {
      if (val === "delete") {
        const newVal = prev.length > 1 ? prev.slice(0, -1) : "0";
        return newVal;
      }

      if (val === ".") {
        if (prev.includes(".")) return prev;
        return prev === "" || prev === "0" ? "0." : prev + ".";
      }

      // Numbers
      let newVal = prev === "0" ? val : prev + val;

      // Restrict decimals
      if (newVal.includes(".")) {
        const [intPart, decimalPart] = newVal.split(".");
        if (decimalPart.length > 2) return prev;
      }

      return newVal;
    });
  };

  const dialogSettleFooter = (
    <>
      <Box
        style={{
          display: "flex",
          gap: 15,
        }}
      >
        <Button size="medium" onClick={onDialogCancel}>
          Cancel
        </Button>
        <AppButton
          disabled={isLoading}
          isLoading={isLoading}
          fullWidth
          size="medium"
          variant="contained"
          onClick={handleSettleClick}
        >
          Settle
        </AppButton>
      </Box>
    </>
  );

  return (
    <Box>
      <AppDialog
        maxWidth="md"
        isOpen={isSettleDialogOpen}
        id={"settle_dialog"}
        title={<>{"Payments"}</>}
        footer={dialogSettleFooter}
      >
        <Box>
          <Box style={{ display: "flex", gap: 20 }}>
            <Box style={{ width: "50%" }}>
              <Box
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                }}
              >
                Order Info
              </Box>
              <Box className="customer-info-wrap">
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <Box>
                    <Avatar sx={{ bgcolor: "grey.800" }} variant="rounded">
                      {dine?.table?.table_name}
                    </Avatar>
                  </Box>
                  <Box>
                    <Box
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      Order #{orderDetails?.order_number} /{" "}
                      {getOrderTypeLabel(orderDetails?.order_type_id)}
                    </Box>
                    {dine?.dine_area_name && (
                      <Chip label={dine?.dine_area_name} size="small" />
                    )}
                  </Box>
                </Box>
                <Box>
                  <Box className="f-12">{formattedDate}</Box>
                  <Box className="f-12">{formattedTime}</Box>
                </Box>
              </Box>
              {customer?.mobile_number && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "12px", paddingBottom: "4px" }}
                  >
                    Cust Name: {customer?.first_name} {customer?.last_name}
                  </Typography>
                </Box>
              )}
              <Box className="settle-order-details">
                <Box
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                  }}
                >
                  Order Details
                </Box>
                <Box>
                  {Array.isArray(allItems) && allItems.length > 0 ? (
                    allItems.map((item) => (
                      <PaymentInfoItemCard
                        key={item?.variant_uid}
                        item={item}
                      />
                    ))
                  ) : (
                    <Box></Box>
                  )}
                  <Box className="settle-item-wrap">
                    <Box className="f-14 settle-item-name">
                      Items({allItems.length})
                    </Box>
                    <Box className="settle-item-qty">
                      <Box>{formatCurrency(20)}</Box>
                    </Box>
                  </Box>
                  {hasDiscount && (
                    <Box className="settle-item-wrap">
                      <Box className="f-14 settle-item-name">Discount</Box>
                      <Box className="settle-item-qty">
                        <Box>- {formatCurrency(20)}</Box>
                      </Box>
                    </Box>
                  )}
                  <Box className="settle-item-wrap">
                    <Box className="f-14 settle-item-name">GST</Box>
                    <Box className="settle-item-qty">
                      <Box>+ {formatCurrency(20)}</Box>
                    </Box>
                  </Box>
                  <Box className="settle-item-wrap">
                    <Box
                      className="f-14 settle-item-name"
                      sx={{ fontWeight: 700 }}
                    >
                      Total
                    </Box>
                    <Box className="settle-item-qty">
                      <Box>{formatCurrency(200)}</Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box style={{ width: "50%" }}>
              <Box
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                }}
              >
                Select a payment method
              </Box>
              <Box
                style={{
                  margin: 10,
                }}
              >
                <OrderPaymentOptions
                  selectedPaymentMethod={selectedPaymentMethod}
                  setSelectedPaymentMethod={setSelectedPaymentMethod}
                />
              </Box>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 15,
                  margin: "25px 10px",
                }}
              >
                <TextField
                  type="text"
                  autoComplete="off"
                  variant="standard"
                  size="small"
                  value={totalAmountValue}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  sx={{
                    width: "130px",
                    "& input": {
                      textAlign: "left",
                      fontSize: 22,
                      fontWeight: 600,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box className="menu-num-pad">
                {[
                  ["1", "2", "3"],
                  ["4", "5", "6"],
                  ["7", "8", "9"],
                  [".", "0", "delete"],
                ].map((row, rowIndex) => (
                  <Box
                    key={rowIndex}
                    display="flex"
                    gap="10px"
                    sx={{ margin: "0 auto" }}
                  >
                    {row.map((key) => (
                      <Button
                        key={key}
                        size="small"
                        onClick={() => handleNumpadClick(key)}
                      >
                        <Box
                          className="num-btn"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {key === "delete" ? (
                            <Iconify icon={"mynaui:delete"} width={20} />
                          ) : (
                            key
                          )}
                        </Box>
                      </Button>
                    ))}
                  </Box>
                ))}
              </Box>
              <Box className="m-amt-content">
                <Box className="m-amt-wrap bb-ccc">
                  <Box>Remaining: </Box>
                  <Box
                    style={{
                      fontWeight: 600,
                    }}
                  >
                    {formatCurrency(remainingAmount)}
                  </Box>
                </Box>
                <Box className="divider"></Box>
                <Box className="m-amt-wrap">
                  <Box>Total: </Box>
                  <Box
                    style={{
                      fontWeight: 600,
                    }}
                  >
                    {formatCurrency(totalAmount)}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </AppDialog>
    </Box>
  );
}

export default PaymentInfo;
